import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Download, FileText, Filter } from 'lucide-react'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore'

interface IncidentRow {
  id: string; incident_number: string; date_occurred: string
  person_name: string; person_dob: string; person_address: string
  person_relationship: string
  injury_body_part: string; injury_nature: string; injury_mechanism: string
  description: string
  medical_treatment: number; lost_time: number; restricted_work: number; first_aid_only: number
  days_lost: number; category: string
}

function calcAge(dob: string): string {
  if (!dob) return ''
  const born = new Date(dob), now = new Date()
  let age = now.getFullYear() - born.getFullYear()
  const m = now.getMonth() - born.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < born.getDate())) age--
  return isNaN(age) || age < 0 ? '' : String(age)
}

function outcomeLabel(row: IncidentRow): string {
  if (row.lost_time) return 'LTI'
  if (row.medical_treatment) return 'MTI'
  if (row.restricted_work) return 'RWI'
  if (row.first_aid_only) return 'FAI'
  return '—'
}

function exportCsv(rows: IncidentRow[]) {
  const headers = ['INC #', 'Date', 'Person Name', 'Age', 'Address', 'Relationship', 'Nature of Injury', 'Body Part', 'Mechanism', 'Outcome', 'Days Lost', 'Description']
  const lines = [headers.join(','), ...rows.map(r => [
    r.incident_number,
    r.date_occurred?.slice(0, 10) || '',
    `"${r.person_name || ''}"`,
    calcAge(r.person_dob),
    `"${r.person_address || ''}"`,
    r.person_relationship || '',
    `"${r.injury_nature || ''}"`,
    `"${r.injury_body_part || ''}"`,
    `"${r.injury_mechanism || ''}"`,
    outcomeLabel(r),
    r.days_lost || 0,
    `"${(r.description || '').replace(/"/g, '""')}"`,
  ].join(','))]
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a'); a.href = url; a.download = `injury-register-${new Date().toISOString().slice(0,10)}.csv`; a.click()
  URL.revokeObjectURL(url)
}

export default function InjuryRegister() {
  const { accessToken } = useAuthStore()
  const [rows, setRows] = useState<IncidentRow[]>([])
  const [loading, setLoading] = useState(true)
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().slice(0, 10)
  })
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10))
  const [pdfLoading, setPdfLoading] = useState(false)
  const [xlsxLoading, setXlsxLoading] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const { data } = await api.get(`/incidents/register?from=${from}&to=${to}`)
      setRows(data.data || data)
    } catch { setRows([]) }
    setLoading(false)
  }

  useEffect(() => { load() }, [from, to])

  const downloadPdf = async () => {
    setPdfLoading(true)
    try {
      const resp = await fetch(`/api/incidents/register.pdf?start=${from}&end=${to}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!resp.ok) throw new Error()
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url; a.download = `injury-register-${from}-to-${to}.pdf`; a.click()
      URL.revokeObjectURL(url)
    } catch { alert('PDF generation failed. Try again.') }
    setPdfLoading(false)
  }

  const downloadXlsx = async () => {
    setXlsxLoading(true)
    try {
      const resp = await fetch(`/api/incidents/register/xlsx?start=${from}&end=${to}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!resp.ok) throw new Error()
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url; a.download = `injury-register-${from}-to-${to}.xlsx`; a.click()
      URL.revokeObjectURL(url)
    } catch { alert('Excel export failed. Try again.') }
    setXlsxLoading(false)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Register of Injuries</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>WHS Act — mandatory record of all work-related injuries and illnesses</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => exportCsv(rows)} className="btn btn-secondary flex items-center gap-2">
            <Download size={15} /> CSV
          </button>
          <button onClick={downloadXlsx} disabled={xlsxLoading} className="btn btn-secondary flex items-center gap-2">
            <FileText size={15} style={{ color: 'var(--blue)' }} /> {xlsxLoading ? 'Exporting…' : 'Excel'}
          </button>
          <button onClick={downloadPdf} disabled={pdfLoading} className="btn btn-secondary flex items-center gap-2">
            <FileText size={15} style={{ color: 'var(--red)' }} /> {pdfLoading ? 'Generating…' : 'PDF'}
          </button>
          <Link to="/incidents/new" className="btn btn-primary">+ Report Incident</Link>
        </div>
      </div>

      {/* Date range filter */}
      <div className="card flex flex-wrap items-end gap-4">
        <Filter size={16} style={{ color: 'var(--text-muted)', marginBottom: '0.25rem' }} />
        <div><label className="text-xs mb-1 block" style={{ color: 'var(--text-muted)' }}>From</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ width: 'auto' }} /></div>
        <div><label className="text-xs mb-1 block" style={{ color: 'var(--text-muted)' }}>To</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ width: 'auto' }} /></div>
        <span className="text-sm pb-0.5" style={{ color: 'var(--text-muted)' }}>{rows.length} {rows.length === 1 ? 'entry' : 'entries'}</span>
      </div>

      {/* Register table */}
      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        {loading ? (
          <div className="p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <p className="font-medium mb-1" style={{ color: 'var(--text)' }}>No injury records in this period</p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>The Register captures incidents where a person suffered a work-related injury or illness.</p>
          </div>
        ) : (
          <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                {['INC #', 'Date', 'Person Name', 'Age', 'Address', 'Nature of Injury', 'Body Part', 'Mechanism', 'Outcome', 'Days Lost', ''].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-semibold text-xs" style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--bg)' }}>
                  <td className="px-4 py-3 font-mono font-semibold" style={{ color: 'var(--accent)', whiteSpace: 'nowrap' }}>{r.incident_number}</td>
                  <td className="px-4 py-3 whitespace-nowrap" style={{ color: 'var(--text)' }}>{r.date_occurred?.slice(0, 10) || '—'}</td>
                  <td className="px-4 py-3 font-medium" style={{ color: 'var(--text)', minWidth: '140px' }}>{r.person_name || <span style={{ color: 'var(--text-muted)' }}>Not recorded</span>}</td>
                  <td className="px-4 py-3 text-center" style={{ color: 'var(--text)' }}>{calcAge(r.person_dob) || '—'}</td>
                  <td className="px-4 py-3" style={{ color: 'var(--text-muted)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.person_address || '—'}</td>
                  <td className="px-4 py-3" style={{ color: 'var(--text)' }}>{r.injury_nature || '—'}</td>
                  <td className="px-4 py-3" style={{ color: 'var(--text)' }}>{r.injury_body_part || '—'}</td>
                  <td className="px-4 py-3" style={{ color: 'var(--text)' }}>{r.injury_mechanism || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`badge ${outcomeLabel(r) === 'LTI' ? 'badge-high' : outcomeLabel(r) === 'MTI' ? 'badge-medium' : 'badge-low'}`}>
                      {outcomeLabel(r)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center" style={{ color: 'var(--text)' }}>{r.days_lost || 0}</td>
                  <td className="px-4 py-3">
                    <Link to={`/incidents/${r.id}`} className="text-xs font-medium" style={{ color: 'var(--accent)' }}>View</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-xs p-4 rounded-xl" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
        <strong>WHS Act 2011 — Section 38.</strong> A person conducting a business or undertaking must keep a register of injuries. The register must be accessible to workers and must include the name and address of the person, the date of injury, the nature of the injury and the circumstances in which it occurred. Records must be retained for at least 5 years.
      </div>
    </div>
  )
}
